import React, { useEffect, useRef, useState } from "react";
import {
    motion,
    useScroll,
    useTransform,
    useReducedMotion,
    useSpring,
    useMotionValue,
} from "framer-motion";
import { Play, Camera, Film } from "lucide-react";
import { DustParticles } from "@/components/DustParticles";

const EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });

    // Layered parallax — many depths
    const yTitle = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -90]);
    const yGlow = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -180]);
    const yAtom = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -50]);
    const yHud = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 30]);
    const scaleTitle = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.06]);
    const blurBg = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 6]);
    const filter = useTransform(blurBg, (b) => `blur(${b}px)`);
    const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.1]);

    // Smooth cursor-driven light position (for projector light center)
    const mx = useMotionValue(50);
    const my = useMotionValue(35);
    const mxSpring = useSpring(mx, { stiffness: 60, damping: 18, mass: 0.6 });
    const mySpring = useSpring(my, { stiffness: 60, damping: 18, mass: 0.6 });

    const heroRef = useRef(null);
    useEffect(() => {
        const el = heroRef.current;
        if (!el) return;
        const onMove = (e) => {
            const r = el.getBoundingClientRect();
            const nx = ((e.clientX - r.left) / r.width) * 100;
            const ny = ((e.clientY - r.top) / r.height) * 100;
            mx.set(nx);
            my.set(ny);
        };
        el.addEventListener("mousemove", onMove);
        return () => el.removeEventListener("mousemove", onMove);
    }, [mx, my]);

    // Bind smoothed positions to CSS vars
    useEffect(() => {
        const el = heroRef.current;
        if (!el) return;
        const unX = mxSpring.on("change", (v) => el.style.setProperty("--gx", `${v}%`));
        const unY = mySpring.on("change", (v) => el.style.setProperty("--gy", `${v}%`));
        return () => {
            unX();
            unY();
        };
    }, [mxSpring, mySpring]);

    // Live timecode HH:MM:SS:FF (24fps)
    const [tc, setTc] = useState("00:00:00:00");
    useEffect(() => {
        const start = performance.now();
        let raf;
        const tick = (now) => {
            const ms = now - start;
            const totalFrames = Math.floor((ms / 1000) * 24);
            const ff = totalFrames % 24;
            const totalSec = Math.floor(totalFrames / 24);
            const ss = totalSec % 60;
            const mm = Math.floor(totalSec / 60) % 60;
            const hh = Math.floor(totalSec / 3600);
            const pad = (n) => String(n).padStart(2, "0");
            setTc(`${pad(hh)}:${pad(mm)}:${pad(ss)}:${pad(ff)}`);
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, []);

    return (
        <section
            id="hero"
            ref={ref}
            data-testid="hero-section"
            className="relative min-h-[100svh] w-full overflow-hidden bg-black"
        >
            <div
                ref={heroRef}
                className="relative h-[100svh] w-full"
                style={{ "--gx": "50%", "--gy": "30%" }}
            >
                {/* === LAYER 0: deep base gradient (very subtle, behind everything) === */}
                <motion.div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{ filter }}
                >
                    <div
                        className="absolute inset-0"
                        style={{
                            background:
                                "radial-gradient(120% 80% at 50% -10%, hsla(38,92%,62%,0.08), transparent 50%), radial-gradient(80% 60% at 50% 110%, hsla(18,86%,58%,0.08), transparent 60%), linear-gradient(180deg, #000 0%, hsl(240 8% 5%) 60%, #000 100%)",
                        }}
                    />
                </motion.div>

                {/* === LAYER 1: hairline grid === */}
                <motion.div
                    style={{ y: yHud }}
                    className="absolute inset-0 hairline-grid opacity-30"
                    aria-hidden
                />

                {/* === LAYER 3: projector light cone (large, soft) === */}
                <motion.div
                    style={{ y: yGlow }}
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                >
                    <div
                        className="absolute inset-0 mix-blend-screen"
                        style={{
                            background:
                                "radial-gradient(900px circle at var(--gx, 50%) var(--gy, 30%), hsla(38,92%,62%,0.22), transparent 55%), radial-gradient(800px circle at 78% 18%, hsla(176,62%,44%,0.14), transparent 60%), radial-gradient(700px circle at 22% 88%, hsla(18,86%,58%,0.12), transparent 65%)",
                        }}
                    />
                    {/* projector cone shaft */}
                    <div
                        className="absolute -top-10 left-1/2 -translate-x-1/2 w-[60vw] h-[120vh] opacity-[0.10] mix-blend-screen"
                        style={{
                            background:
                                "linear-gradient(180deg, hsla(38,92%,62%,0.55) 0%, hsla(38,92%,62%,0.18) 35%, transparent 75%)",
                            clipPath: "polygon(46% 0, 54% 0, 80% 100%, 20% 100%)",
                            filter: "blur(40px)",
                        }}
                    />
                    {/* slow rotating light sweep */}
                    <motion.div
                        animate={reduce ? {} : { rotate: 360 }}
                        transition={{ duration: 60, ease: "linear", repeat: Infinity }}
                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[160vw] h-[160vh] opacity-[0.08] mix-blend-screen"
                        style={{
                            background:
                                "conic-gradient(from 0deg, transparent 0%, hsla(38,92%,62%,0.3) 12%, transparent 24%, transparent 100%)",
                            filter: "blur(60px)",
                        }}
                    />
                </motion.div>

                {/* === LAYER 4: dust particles === */}
                <DustParticles count={130} />

                {/* === LAYER 5: atom motif (faded, rotating) === */}
                <motion.div
                    style={{ y: yAtom, opacity }}
                    aria-hidden
                    className="absolute inset-0 flex items-center justify-center pointer-events-none"
                >
                    <motion.div
                        animate={reduce ? {} : { rotate: 360 }}
                        transition={{ duration: 140, ease: "linear", repeat: Infinity }}
                        className="opacity-[0.07] w-[78vmin] h-[78vmin]"
                    >
                        <AtomSVG />
                    </motion.div>
                </motion.div>

                {/* === LAYER 6: vignette === */}
                <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(120% 90% at 50% 50%, transparent 40%, rgba(0,0,0,0.55) 90%, rgba(0,0,0,0.9) 100%)",
                    }}
                />

                {/* === LAYER 7: film-strip edge sprockets === */}
                <FilmStripEdge side="left" />
                <FilmStripEdge side="right" />

                {/* === LAYER 8: Cinemascope letterbox bar (bottom only; top frame is the nav) === */}
                <motion.div
                    initial={{ y: reduce ? 0 : "100%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1.0, ease: EASE, delay: 0.1 }}
                    className="absolute bottom-0 left-0 right-0 h-[10vh] bg-black z-20 pointer-events-none border-t border-white/[0.05]"
                    aria-hidden
                />

                {/* === LAYER 9: Top HUD (sits below the nav) === */}
                <div className="absolute top-16 left-0 right-0 h-12 z-30 px-5 sm:px-8 flex items-center justify-between text-white pointer-events-none border-b border-white/[0.04]">
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.1, duration: 0.6, ease: EASE }}
                        className="flex items-center gap-3"
                    >
                        <span
                            className="inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent-gold))]"
                            style={{ animation: "rec-pulse 1.4s ease-in-out infinite" }}
                        />
                        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white">
                            REC <span className="text-[hsl(var(--accent-gold))]">●</span>{" "}
                            <span className="text-[hsl(var(--text-secondary))]">{tc}</span>
                        </span>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.2, duration: 0.6, ease: EASE }}
                        className="hidden sm:flex items-center gap-6"
                    >
                        <ColorBars />
                        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--text-muted))]">
                            CH1 · 2.39:1 · 24 FPS
                        </span>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.3, duration: 0.6, ease: EASE }}
                        className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] text-white"
                    >
                        <span className="text-[hsl(var(--text-muted))]">REEL</span>
                        <span className="px-2 py-[2px] border border-white/20 text-white">01</span>
                        <span className="text-[hsl(var(--text-muted))]">/</span>
                        <span className="text-[hsl(var(--text-muted))]">SC</span>
                        <span className="px-2 py-[2px] border border-white/20 text-white">001</span>
                    </motion.div>
                </div>

                {/* === LAYER 10: Iris reveal mask === */}
                <motion.div
                    aria-hidden
                    className="absolute inset-0 z-[25] pointer-events-none bg-black"
                    initial={
                        reduce
                            ? { opacity: 0 }
                            : {
                                  clipPath: "circle(0% at 50% 50%)",
                                  WebkitClipPath: "circle(0% at 50% 50%)",
                                  opacity: 1,
                              }
                    }
                    animate={
                        reduce
                            ? { opacity: 0 }
                            : {
                                  clipPath: "circle(140% at 50% 50%)",
                                  WebkitClipPath: "circle(140% at 50% 50%)",
                                  opacity: 0,
                              }
                    }
                    transition={{ duration: 1.6, ease: EASE, delay: 0.05 }}
                    style={{
                        background:
                            "radial-gradient(circle at center, transparent 0%, transparent 35%, #000 60%)",
                    }}
                />

                {/* === LAYER 11: Center title card === */}
                <motion.div
                    style={{ y: yTitle, opacity, scale: scaleTitle }}
                    className="relative z-10 h-full flex flex-col justify-center items-center px-6"
                >
                    <div className="max-w-7xl mx-auto w-full">
                        {/* Tagline ribbon */}
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.0, duration: 0.7, ease: EASE }}
                            className="mb-7 flex items-center justify-center gap-4"
                        >
                            <span className="h-px w-10 bg-white/30" />
                            <span className="inline-flex items-center gap-2.5 px-3 py-1.5 border border-white/15 rounded-sm bg-black/40 backdrop-blur-[2px]">
                                <Camera className="h-3 w-3 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
                                <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--text-secondary))]">
                                    Full Services Movie Production Co.
                                </span>
                            </span>
                            <span className="h-px w-10 bg-white/30" />
                        </motion.div>

                        {/* Title */}
                        <h1
                            data-testid="hero-title"
                            className="font-display text-center leading-[0.86] tracking-tight text-[clamp(3rem,12.5vw,10.5rem)] text-white"
                            style={{
                                textShadow:
                                    "0 4px 30px rgba(0,0,0,0.5), 0 0 80px hsla(38,92%,62%,0.08)",
                            }}
                        >
                            <KineticLine text="STUDIO" />
                            <span className="sr-only"> </span>
                            <KineticLine text="SHAKTHI" delay={0.18} />
                        </h1>

                        {/* Tamil + English subtitle */}
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.05, duration: 0.85, ease: EASE }}
                            className="mt-7 flex flex-col items-center"
                        >
                            <div className="flex items-center gap-4">
                                <span className="h-px w-12 bg-[hsl(var(--accent-gold))]/50" />
                                <span className="font-display text-xl sm:text-2xl tracking-[0.3em] text-[hsl(var(--accent-gold))] uppercase">
                                    Karpanai Virpanai
                                </span>
                                <span className="h-px w-12 bg-[hsl(var(--accent-gold))]/50" />
                            </div>
                            <span className="mt-2 font-mono text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--text-muted))]">
                                Imagine, Create, Distribute
                            </span>
                        </motion.div>

                        {/* CTAs */}
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.2, duration: 0.9, ease: EASE }}
                            className="mt-12 flex items-center justify-center gap-3 sm:gap-4 flex-wrap"
                        >
                            <a
                                href="#projects"
                                data-testid="hero-primary-cta-button"
                                className="group inline-flex items-center gap-3 px-6 py-3.5 rounded-sm bg-white text-black font-mono text-[10px] uppercase tracking-[0.28em] hover:bg-[hsl(var(--accent-gold))] transition-colors"
                            >
                                <Play className="h-3.5 w-3.5" fill="currentColor" strokeWidth={0} />
                                View the Reel
                            </a>
                            <a
                                href="#contact"
                                data-testid="hero-secondary-cta-button"
                                className="inline-flex items-center gap-3 px-6 py-3.5 rounded-sm border border-white/20 text-white font-mono text-[10px] uppercase tracking-[0.28em] hover:border-white/50 hover:bg-white/[0.04] transition-colors"
                            >
                                <Film className="h-3.5 w-3.5" strokeWidth={1.5} />
                                Book a Screening
                            </a>
                        </motion.div>
                    </div>
                </motion.div>

                {/* === LAYER 12: Bottom HUD === */}
                <div className="absolute bottom-0 left-0 right-0 h-[10vh] z-30 px-5 sm:px-8 flex items-center justify-between text-white pointer-events-none">
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.3, duration: 0.6, ease: EASE }}
                        className="flex items-center gap-3"
                    >
                        <SlateBadge />
                        <span
                            data-testid="hero-scroll-cue"
                            className="hidden sm:inline-flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--text-muted))]"
                        >
                            <span className="inline-block h-3 w-px bg-[hsl(var(--text-muted))]" />
                            Scroll for next scene
                        </span>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.35, duration: 0.6, ease: EASE }}
                        className="text-right font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--text-muted))]"
                    >
                        <div>To Disrupt the Status Quo</div>
                        <div className="text-[hsl(var(--accent-gold))]/80">
                            MMXXV &middot; CHENNAI
                        </div>
                    </motion.div>
                </div>

                {/* corner registration marks */}
                <CornerMarks />

                <style>{`
                    @keyframes rec-pulse {
                        0%, 100% { opacity: 0.35; box-shadow: 0 0 0 0 hsla(38,92%,62%,0); }
                        50% { opacity: 1; box-shadow: 0 0 0 4px hsla(38,92%,62%,0.18); }
                    }
                `}</style>
            </div>
        </section>
    );
};

const KineticLine = ({ text, delay = 0 }) => {
    const reduce = useReducedMotion();
    const letters = text.split("");
    return (
        <span className="block overflow-hidden">
            <span className="inline-block" aria-label={text}>
                {letters.map((ch, i) => (
                    <motion.span
                        key={i}
                        initial={{ y: reduce ? 0 : "110%", opacity: reduce ? 1 : 0 }}
                        animate={{ y: "0%", opacity: 1 }}
                        transition={{
                            delay: 0.65 + delay + i * 0.045,
                            duration: reduce ? 0.4 : 0.9,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                        className="inline-block"
                    >
                        {ch}
                    </motion.span>
                ))}
            </span>
        </span>
    );
};

const ColorBars = () => (
    <span className="inline-flex items-center h-3 rounded-[2px] overflow-hidden border border-white/10">
        {[
            "hsl(0 0% 78%)",
            "hsl(52 90% 60%)",
            "hsl(176 62% 44%)",
            "hsl(120 50% 45%)",
            "hsl(290 50% 55%)",
            "hsl(0 70% 55%)",
            "hsl(220 70% 55%)",
        ].map((c, i) => (
            <span key={i} style={{ background: c }} className="block h-3 w-2" />
        ))}
    </span>
);

const SlateBadge = () => (
    <span className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-sm bg-black/60 border border-white/15 backdrop-blur-[2px]">
        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white">TAKE</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">01</span>
    </span>
);

const FilmStripEdge = ({ side = "left" }) => (
    <div
        aria-hidden
        className={`hidden md:flex absolute top-28 bottom-[10vh] ${
            side === "left" ? "left-0" : "right-0"
        } w-8 z-10 flex-col items-center justify-around py-4 pointer-events-none border-${
            side === "left" ? "r" : "l"
        } border-white/5 bg-black/30`}
    >
        {Array.from({ length: 16 }).map((_, i) => (
            <span key={i} className="block h-3.5 w-3 rounded-[2px] bg-white/[0.08]" />
        ))}
    </div>
);

const CornerMarks = () => (
    <div className="absolute inset-0 pointer-events-none z-[28]" aria-hidden>
        {[
            "top-28 left-0",
            "top-28 right-0 rotate-90",
            "bottom-[10vh] left-0 -rotate-90",
            "bottom-[10vh] right-0 rotate-180",
        ].map((pos, i) => (
            <svg
                key={i}
                className={`absolute ${pos} w-6 h-6 m-3 sm:m-4 opacity-50`}
                viewBox="0 0 24 24"
                fill="none"
            >
                <path
                    d="M2 8 L2 2 L8 2"
                    stroke="hsl(38 92% 62%)"
                    strokeWidth="1"
                    strokeLinecap="square"
                />
            </svg>
        ))}
    </div>
);

const AtomSVG = () => (
    <svg viewBox="0 0 200 200" className="w-full h-full" fill="none">
        <ellipse cx="100" cy="100" rx="80" ry="32" stroke="hsl(0 0% 96%)" strokeWidth="1" />
        <ellipse
            cx="100"
            cy="100"
            rx="80"
            ry="32"
            stroke="hsl(0 0% 96%)"
            strokeWidth="1"
            transform="rotate(60 100 100)"
        />
        <ellipse
            cx="100"
            cy="100"
            rx="80"
            ry="32"
            stroke="hsl(0 0% 96%)"
            strokeWidth="1"
            transform="rotate(120 100 100)"
        />
        <circle cx="100" cy="100" r="9" fill="hsl(38 92% 62%)" />
        <circle cx="30" cy="80" r="5" fill="hsl(0 70% 55%)" />
        <circle cx="170" cy="120" r="5" fill="hsl(220 70% 55%)" />
        <circle cx="60" cy="160" r="5" fill="hsl(140 55% 45%)" />
    </svg>
);
